import {
    Component, DecoratorRouter, IFrame, Layout, LayoutCacheAccessor, Overlay, Panel, PanelStaticError, ScreenStack, Spinner,
    syncDocumentKeyListener
} from "@mcleod/components";
import {
    Api, ApiMetadata, ApiMetadataCacheAccessor, ArrayUtil, AuthToken, ClientRegistration, Collection, DynamicLoader, GeneralSettings, LogManager, McLeodContext,
    ModelRow,
    Navigation, RegisteredRoutes, StringUtil, UrlUtil, WindowTitle
} from "@mcleod/core";
import { DataLoadActionHandler } from "../DataLoadActionHandler";
import { MainPage } from "../mainpage/MainPage";
import { RouterProps } from "./RouterProps";

const log = LogManager.getLogger("common.Router");

/* This call is how we inform the History module that the main page's Router is
 * what will handle be used to display new content as the user navigates the site */
Navigation.setNavigationHandler({
    displayRoute: (path: string, props: any) => {
        const mainPage = MainPage.instance;
        if (mainPage == null)
            window.location.pathname = path;
        else {
            // window.history.pushState(props, "McLeod", path);
            if (mainPage.router == null) {
                path = DynamicLoader.getRouteFromURL(path);
                const cls = DynamicLoader.getClassForPath(path);
                const inst = new cls(props);
                mainPage.removeAll();
                mainPage.add(inst);
            }
            else
                Router.instance.displayRoute(path, props);
        }
    },
    getRoute: () => {
        const router = Router.instance;
        let result = router._route;
        if (!StringUtil.isEmptyString(router._routeSearchString))
            result += router._routeSearchString;
        return result;
    }
});

/**
 * The Router class is responsible for figuring out which Layout to display based on the user's
 * current browser address.  It dynamically loads the class specified by the address and
 */
export abstract class Router extends Panel {
    private static _instance: Router;
    _isMainRouter: boolean;
    _route: string;
    _routeSearchString: string;
    settingsRoot: string;

    protected constructor(props: Partial<RouterProps>) {
        super({
            id: props?.id == null ? "mainRouter" : props.id,
            fillRow: true,
            fillHeight: true,
            padding: 24,
            paddingRight: 18,
            scrollX: true,
            scrollY: true,
            ...props
        });

        // do not add non-root Routers to the ScreenStack; they should be contained
        // in some other panel/layout that is being displayed within the root Router.
        if (props?.isMainDocumentRouter !== false) {
            ScreenStack.push(this);
            this._isMainRouter = true;
            McLeodContext.setRouter(this);
            Router._instance = this;
        }
        else
            this._isMainRouter = false;
    }

    public displayRoute(route: string, props?: any) {
        this._populateAuthSettingsThenDisplay(route, props);
    }

    private _populateAuthSettingsThenDisplay(route: string, props?: any) {
        if (props?._auth != null) {
            AuthToken.set(props._auth);
            delete props._auth;
            window.history.replaceState({ props }, "McLeod", window.location.pathname);
        }
        if (AuthToken.isAuthenticated() && GeneralSettings.getSingleton().authSettingsPopulated() === false) {
            ClientRegistration.addUnregisterBeforeUnloadListener();
            const root = this.settingsRoot || "lme/";
            const body = (root === "lme/") ? ClientRegistration.getAuthSettingsBody() : {};
            Api.search(root + "auth-settings", body).then(response => {
                const licensingResult = ClientRegistration.handleLicensingResult(response);
                if (licensingResult === true) {
                    this._displayRoute(route, props);
                }
            }).catch(err => {
                log.info("%cError loading page\n", "background: #222; color: white", err);
                this.setRouterContents(new PanelStaticError({ errorSummary: "Sorry, there was an error loading this page.", devDetail: err + "\n" }));
            });
        }
        else
            this._displayRoute(route, props);
    }

    private _displayRoute(route: string, props?: any) {
        if (route?.indexOf(":") >= 0) {
            this.displayRemoteRoute(route, props);
            return;
        }

        Overlay.hideAllOverlays();
        this.getKeyHandlerGroup().clear();
        const parentRouter = this.getParentRouter();
        this.copyCachesFromParentRouter(parentRouter);
        Navigation.removeAllNavigationListeners();
        props = { ...props, ...UrlUtil.getPropsFromUrl(route) };
        route = DynamicLoader.getRouteFromURL(route);
        this.dismissAllPopups();
        this.removeAll();
        const auth = AuthToken.isAuthenticated();
        const base = GeneralSettings.get().base_url_offset;
        if (base != null && route.startsWith(base)) {
            route = route.substring(base.length);
            if (route.startsWith("/"))
                route = route.substring(1);
        }
        if (route.length === 0 || route === "index.html") {
            route = auth ? RegisteredRoutes.homeRoute : RegisteredRoutes.loginRoute;
        }
        this._route = route;
        this._routeSearchString = window.location.search;
        if (props._undecorated !== "")
            MainPage.instance.pageHeader.syncNavigation();

        this.add(new Spinner());
        const layout = Layout.getLayout(route, props);
        if (props._undecorated !== "")
            this.applyRouterPropsFromPage(layout);
        layout.addLayoutLoadListener(() => {
            const decorated: Component = DecoratorRouter.decoratePage(layout);
            this.setRouterContents(decorated);
            if (this._isMainRouter == true) {
                //if the layout in the main router is changing, the router needs to fill its key handler group
                //this pulls all the key handlers up into a flat collection within the router
                this.fillKeyHandlerGroup(true);

                if (decorated instanceof Layout)
                    WindowTitle.resetWith(() => layout.windowTitle, "Router");
            }
            else {
                // if the layout in a nested router (such as the demo router) is changing, the main router needs
                // to fill its key handler group.  key handlers from the nested router should be pulled up
                // into the main router's flat collection
                Router.instance.fillKeyHandlerGroup(true);
            }
            syncDocumentKeyListener();
        });
        layout.addLoadErrorListener(() => {
            WindowTitle.resetWith(null, "Router (Load Error)");
            this.setRouterContents(layout);
        });

        const dataLoadActionProps = DataLoadActionHandler.gatherFromUrlProps(props);
        if (ArrayUtil.isEmptyArray(dataLoadActionProps) === false) {
            layout.addLayoutDataLoadListener(() => DataLoadActionHandler.handleDataLoadActions(layout, dataLoadActionProps, props));
        }
    }

    private displayRemoteRoute(route: string, props: any) {
        if (route.startsWith("/"))
            route = route.substring(1);
        const iframe = new IFrame({src: route, fillRow: true, fillHeight: true, ...props});
        this.setRouterContents(iframe);
    }

    private setRouterContents(...components: Component[]) {
        this.removeAll();
        this.add(...components);
    }

    private applyRouterPropsFromPage(owner): void {
        if (owner == null || owner.getRouterProps == null)
            this.setProps({ padding: this.defaultRouterPadding });
        else
            this.setProps(owner.getRouterProps());
    }

    public get defaultRouterPadding(): number {
        return 24;
    }

    public static isDisplayingHomePage(): boolean {
        return Router.instance._route == RegisteredRoutes.homeRoute;
    }

    public static get instance(): Router {
        return this._instance;
    }

    private getParentRouter(): Router {
        if (window.opener && !window.opener.closed) {
            try {
                const openerOrigin = window.opener.location.origin;
                if (openerOrigin === window.location.origin) {
                    return window.opener["mcleodContext"]?.router as Router;
                }
            } catch (error) {
                log.error("Error accessing parent window", error);
            }
        }
        return null;
    }

    private copyCachesFromParentRouter(parentRouter: Router) {
        if (parentRouter == null) {
            return;
        }
        if (LayoutCacheAccessor.isEmpty()) {
            LayoutCacheAccessor.putAll(parentRouter.getLayoutCacheCopy());
        }
        if (ApiMetadataCacheAccessor.isEmpty()) {
            ApiMetadataCacheAccessor.putAll(parentRouter.getApiMetaDataCacheCopy());
        }
    }

    getLayoutCacheCopy(): Collection<string> {
        return LayoutCacheAccessor.getCopy();
    }

    getApiMetaDataCacheCopy(): Collection<ApiMetadata> {
        return ApiMetadataCacheAccessor.getCopy();
    }
}
